import { useEffect, useState } from "react";
import { ActiveThemeResponse, type ThemeManifest } from "@crashx/theme-schema";
import { applyThemeTokens } from "@crashx/ui";
import { i18next } from "../../i18n/index.js";

export interface ThemeState {
  theme: ThemeManifest | null;
  error: string | null;
}

/** Refresh saved settings after local edits, tab focus and remote admin changes. */
export function useTheme(slug: string | null): ThemeState {
  const [state, setState] = useState<ThemeState>({ theme: null, error: null });

  useEffect(() => {
    if (slug === null) return;
    let controller: AbortController | null = null;
    let previous = "";
    const refresh = () => {
      controller?.abort();
      const pending = new AbortController();
      controller = pending;
      void fetch("/api/v1/themes/active", {
        credentials: "same-origin",
        cache: "no-store",
        signal: pending.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Active theme unavailable");
          const { manifest } = ActiveThemeResponse.parse(await response.json());
          if (pending.signal.aborted) return;
          const key = JSON.stringify(manifest);
          if (previous !== key) {
            previous = key;
            applyThemeTokens(manifest.tokens);
            setState({ theme: manifest, error: null });
          } else setState((current) => (current.error ? { ...current, error: null } : current));
        })
        .catch(() => {
          if (!pending.signal.aborted)
            setState((current) => ({ ...current, error: i18next.t("adminThemes.errors.load") }));
        });
    };
    refresh();
    window.addEventListener("crashx:theme-changed", refresh);
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") refresh();
    }, 15000);
    return () => {
      controller?.abort();
      window.clearInterval(timer);
      window.removeEventListener("crashx:theme-changed", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [slug]);

  return state;
}
